import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, Check, Sparkles, FileSearch, ClipboardList, Rocket,
  TrendingUp, Shield, Calendar,
} from "lucide-react";
import { PageShell } from "./publicShared";

/* ═══════════════════════════════════════════════
   SERVICES PAGE — Executive Clarity
   6 offerings with descriptions + visual snap
   ═══════════════════════════════════════════════ */

const EYEBROW = "text-[12px] font-semibold tracking-[0.16em] text-[#1a56db] uppercase mb-3";

const SERVICES = [
  {
    key: "opportunity_score",
    tag: "Step 1",
    tag_color: "#059669",
    tag_bg: "#ecfdf5",
    icon: Sparkles,
    title: "AI Opportunity Score",
    price: "Free",
    priceSuffix: "",
    tagline: "Your 5-minute starting point.",
    description:
      "A quick intake form that computes your top 3 workflow opportunities, shows a readiness snapshot, and recommends your next step — with no commitment.",
    bullets: [
      "Takes under 5 minutes",
      "Top 3 workflow opportunities for your industry",
      "Readiness indicators",
      "Personalized next-step recommendation",
    ],
    cta: "Get My Opportunity Score",
    cta_route: "score",
    snap: {
      score: "72",
      label: "Opportunity Score",
      stage: "Ready to pilot",
      chips: ["Invoice processing", "Client onboarding", "Compliance"],
    },
  },
  {
    key: "free_assessment",
    tag: "Step 2 · Free",
    tag_color: "#1a56db",
    tag_bg: "#eff6ff",
    icon: FileSearch,
    title: "Free Preliminary Assessment",
    price: "Free",
    priceSuffix: "· 30-min call",
    tagline: "A no-pressure conversation.",
    description:
      "A qualification call with a 589 Labs consultant to validate your opportunity, discuss fit, and walk you through what a In-Depth Assessment would uncover for your organization.",
    bullets: [
      "30-minute video call with a senior consultant",
      "Review of your Opportunity Snapshot",
      "Tailored recommendation on fit and urgency",
      "No obligation to move forward",
    ],
    cta: "Book Free Call",
    cta_route: "book",
    snap: {
      score: "30",
      label: "Minutes",
      stage: "Consultant-led",
      chips: ["Fit check", "Industry context", "Next-step plan"],
    },
  },
  {
    key: "detailed_assessment",
    tag: "Step 3 · Most popular",
    tag_color: "#1a56db",
    tag_bg: "#eff6ff",
    icon: ClipboardList,
    title: "In-Depth Assessment",
    price: "",
    priceSuffix: "",
    tagline: "The readiness + roadmap engagement.",
    description:
      "A structured deep-dive across your business processes, technology, data, governance, organizational readiness, and measurement. Produces a prioritized action plan and tenant portal setup.",
    bullets: [
      "6-pillar AI readiness evaluation",
      "Prioritized workflow opportunities with ROI modeling",
      "Readiness & security lens",
      "Prioritized action plan",
      "Dedicated customer portal provisioned",
    ],
    cta: "Start In-Depth Assessment",
    cta_route: "book",
    snap: {
      score: "1.76",
      label: "Readiness Score",
      stage: "Significant Improvement",
      chips: ["Action plan", "Portal setup", "Security lens"],
    },
  },
  {
    key: "phase_1",
    tag: "Step 4",
    tag_color: "#7c3aed",
    tag_bg: "#f5f3ff",
    icon: Rocket,
    title: "Phase 1 Implementation",
    price: "$9,995",
    priceSuffix: "· starting at",
    tagline: "Three production workflows, 8–12 weeks.",
    description:
      "End-to-end implementation of your top 3 highest-value workflows. Includes solution design, AI workflow / agent configuration, testing, deployment, handoff materials, and weekly check-ins.",
    bullets: [
      "3 AI workflow implementations",
      "Solution design + validation",
      "Milestones & timeline",
      "KPI dashboard & value tracking",
      "Go-live support + handoff package",
    ],
    cta: "Learn More",
    cta_route: "book",
    snap: {
      score: "3",
      label: "Workflows",
      stage: "Production-ready",
      chips: ["Solution design", "Testing", "KPI tracking"],
    },
  },
  {
    key: "caio_retainer",
    tag: "Ongoing",
    tag_color: "#059669",
    tag_bg: "#ecfdf5",
    icon: TrendingUp,
    title: "CAIO Retainer",
    price: "$3,995",
    priceSuffix: "/month · starting at",
    tagline: "Your fractional Chief AI Officer.",
    description:
      "Keep your AI program moving after launch. Monitoring, optimization, quarterly roadmap refreshes, and a guaranteed micro-enhancement every month — delivered by a senior consultant.",
    bullets: [
      "2 hours/week with a senior consultant",
      "Monitoring & optimization",
      "Quarterly roadmap refresh",
      "1 micro-enhancement per month",
      "Dirk DaCosta briefing (Advanced tier)",
    ],
    cta: "Learn More",
    cta_route: "book",
    snap: {
      score: "2",
      label: "Hours / Week",
      stage: "Active CAIO",
      chips: ["Monitor", "Optimize", "Enhance"],
    },
  },
  {
    key: "caporilli",
    tag: "Education · Specialized",
    tag_color: "#b45309",
    tag_bg: "#fffbeb",
    icon: Shield,
    title: "MySchoolRx Utility",
    price: "$995",
    priceSuffix: "· starting at",
    tagline: "Board- and authorizer-facing school diagnostic.",
    description:
      "A controlled quarterly attestation instrument for evaluating K-12 school quality, operational health, and risk across 7 pillars and 66 diagnostic questions — with governance, What-If simulator, AI corrective action plans, and NJEA oversight portal.",
    bullets: [
      "7-pillar quality evaluation (66 questions)",
      "Quarterly attestation cycles",
      "Contributor / approver governance workflow",
      "What-If scenario simulator",
      "AI-generated corrective action plans",
      "NJEA oversight portal",
    ],
    cta: "Learn More",
    cta_route: "caporilli",
    snap: {
      score: "74",
      label: "School Health Score",
      stage: "Caution",
      chips: ["7 pillars", "66 questions", "NJEA portal"],
    },
  },
];

const Hero = ({ navigate }) => (
  <section className="bg-white py-16 lg:py-20" data-testid="services-hero">
    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-10 text-center">
      <div className="inline-flex items-center gap-2 rounded-full bg-[#e8eefb] px-4 py-1.5 text-[13px] font-semibold text-[#1a56db] mb-6">
        <span className="h-1.5 w-1.5 rounded-full bg-[#1a56db]" />
        How we work
      </div>
      <h1 className="text-[42px] sm:text-[52px] lg:text-[60px] font-bold text-[#111827] leading-[1.05] tracking-tight" style={{ letterSpacing: "-1.5px" }}>
        Six services.<br />One clear path.
      </h1>
      <p className="mt-6 text-[16px] sm:text-[17px] text-[#4b5563] leading-[1.7] max-w-[640px] mx-auto">
        Every 589 Labs engagement starts with a free, no-commitment snapshot
        and scales only when the return is clear. Pick where to start — we'll
        tell you honestly if you're ready for the next step.
      </p>

      <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
        <Button
          onClick={() => {
            navigate("/");
            setTimeout(() => {
              const el = document.getElementById("opportunity-score");
              if (el) {
                const y = el.getBoundingClientRect().top + window.pageYOffset - 96;
                window.scrollTo({ top: y, behavior: "smooth" });
              }
            }, 120);
          }}
          className="h-12 rounded-lg bg-[#1a56db] px-6 text-white text-[14px] font-semibold hover:bg-[#143faa] shadow-none"
          data-testid="services-hero-cta-score"
        >
          Start with a Free Score
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          onClick={() => navigate("/contact?intent=book")}
          className="h-12 rounded-lg border border-[#e5e7eb] bg-white px-6 text-[#111827] text-[14px] font-semibold hover:bg-[#f9fafb] shadow-none"
          data-testid="services-hero-cta-book"
        >
          <Calendar className="mr-2 h-4 w-4" />
          Book a Free Call
        </Button>
      </div>
    </div>
  </section>
);

const ServiceRow = ({ service, idx, navigate }) => {
  const Icon = service.icon;
  const isEven = idx % 2 === 0;

  const handleClick = () => {
    if (service.cta_route === "score") {
      navigate("/");
      setTimeout(() => {
        const el = document.getElementById("opportunity-score");
        if (el) {
          const y = el.getBoundingClientRect().top + window.pageYOffset - 96;
          window.scrollTo({ top: y, behavior: "smooth" });
        }
      }, 120);
    } else if (service.cta_route === "caporilli") {
      navigate("/myschoolrx");
      setTimeout(() => window.scrollTo({ top: 0, behavior: "instant" }), 100);
    } else {
      navigate("/contact?intent=book");
      setTimeout(() => window.scrollTo({ top: 0, behavior: "instant" }), 100);
    }
  };

  return (
    <div
      id={`service-${service.key}`}
      className={`grid gap-10 lg:gap-16 lg:grid-cols-2 items-center scroll-mt-24 ${isEven ? "" : "lg:[&>div:first-child]:order-2"}`}
      data-testid={`service-row-${service.key}`}
    >
      {/* Copy side */}
      <div>
        <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-bold tracking-[0.08em] uppercase mb-4"
          style={{ backgroundColor: service.tag_bg, color: service.tag_color }}>
          {service.tag}
        </div>

        <div className="flex items-center gap-3 mb-3">
          <div className="h-11 w-11 rounded-xl bg-[#e8eefb] flex items-center justify-center shrink-0">
            <Icon className="h-5 w-5 text-[#1a56db]" />
          </div>
          <h2 className="text-[28px] lg:text-[32px] font-bold text-[#111827] leading-[1.15] tracking-tight" style={{ letterSpacing: "-0.5px" }}>
            {service.title}
          </h2>
        </div>

        <div className="flex items-baseline gap-2 mb-4">
          <span className="text-[26px] font-bold text-[#111827]">{service.price}</span>
          {service.priceSuffix && (
            <span className="text-[13px] text-[#9ca3af]">{service.priceSuffix}</span>
          )}
        </div>

        <p className="text-[16px] text-[#111827] font-semibold mb-3">{service.tagline}</p>
        <p className="text-[15px] text-[#4b5563] leading-[1.7] mb-6">{service.description}</p>

        <ul className="space-y-3 mb-7">
          {service.bullets.map((b) => (
            <li key={b} className="flex gap-3 text-[14px] text-[#374151]">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#10b981]" strokeWidth={3} />
              <span>{b}</span>
            </li>
          ))}
        </ul>

        <Button
          onClick={handleClick}
          className="h-12 rounded-lg bg-[#1a56db] px-6 text-white text-[14px] font-semibold hover:bg-[#143faa] shadow-none"
          data-testid={`service-cta-${service.key}`}
        >
          {service.cta}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>

      {/* Visual snap side */}
      <div className="relative">
        <div className="rounded-2xl border border-[#e5e7eb] bg-white p-7 shadow-[0_8px_32px_rgba(17,24,39,0.06)]">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[10px] font-bold tracking-[0.14em] text-[#9ca3af] uppercase">
              Sample Output
            </div>
            <span className="inline-flex items-center gap-1 rounded-full bg-[#f9fafb] border border-[#e5e7eb] px-2.5 py-0.5 text-[10px] font-semibold text-[#6b7280]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#1a56db]" />
              Preview
            </span>
          </div>

          <div className="flex items-end gap-3 mb-4">
            <div className="text-[56px] font-bold text-[#111827] tracking-tight leading-[1]" style={{ letterSpacing: "-1px" }}>
              {service.snap.score}
            </div>
            <div className="pb-2">
              <div className="text-[11px] font-semibold tracking-[0.08em] text-[#9ca3af] uppercase">
                {service.snap.label}
              </div>
            </div>
          </div>

          <div className="inline-flex items-center gap-1.5 rounded-full bg-[#ecfdf5] px-3 py-1 text-[11px] font-semibold text-[#059669] mb-6">
            <Check className="h-3 w-3" strokeWidth={3} />
            {service.snap.stage}
          </div>

          <div className="space-y-2.5">
            {service.snap.chips.map((chip, i) => (
              <div key={chip} className="flex items-center justify-between">
                <span className="text-[13px] text-[#111827]">{chip}</span>
                <div className="flex-1 mx-3 h-[6px] rounded-full bg-[#f3f4f6] overflow-hidden max-w-[180px]">
                  <div
                    className="h-full rounded-full bg-[#1a56db]"
                    style={{ width: `${90 - i * 14}%` }}
                  />
                </div>
                <span className="text-[12px] font-semibold text-[#4b5563]">{90 - i * 14}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const ServicesContent = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Scroll to anchor when hash changes (e.g. /services#service-caio_retainer)
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          const y = el.getBoundingClientRect().top + window.pageYOffset - 96;
          window.scrollTo({ top: y, behavior: "smooth" });
        }
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [location.hash]);

  return (
    <PageShell>
      <Hero navigate={navigate} />

      {/* Jump nav */}
      <section className="bg-[#f9fafb] border-y border-[#e5e7eb] py-5" data-testid="services-jump-nav">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 flex flex-wrap items-center justify-center gap-2">
          {SERVICES.map((s) => (
            <a
              key={s.key}
              href={`#service-${s.key}`}
              className="inline-flex items-center rounded-full bg-white border border-[#e5e7eb] px-3.5 py-1.5 text-[12px] font-semibold text-[#374151] hover:border-[#1a56db] hover:text-[#1a56db] transition-colors"
            >
              {s.title}
            </a>
          ))}
        </div>
      </section>

      {/* Service rows */}
      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 space-y-20 lg:space-y-28">
          {SERVICES.map((s, idx) => (
            <ServiceRow key={s.key} service={s} idx={idx} navigate={navigate} />
          ))}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-[#f9fafb] border-t border-[#e5e7eb] py-16 lg:py-20" data-testid="services-bottom-cta">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-10 text-center">
          <h2 className="text-[32px] sm:text-[38px] font-bold text-[#111827] leading-[1.15] tracking-tight" style={{ letterSpacing: "-0.5px" }}>
            Not sure where to start?
          </h2>
          <p className="mt-4 text-[16px] text-[#4b5563] leading-[1.7] max-w-[560px] mx-auto">
            Get your free AI Opportunity Score in under 5 minutes. We'll
            recommend the right next step based on your industry, size, and
            workflow urgency.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button
              onClick={() => {
                navigate("/");
                setTimeout(() => {
                  const el = document.getElementById("opportunity-score");
                  if (el) {
                    const y = el.getBoundingClientRect().top + window.pageYOffset - 96;
                    window.scrollTo({ top: y, behavior: "smooth" });
                  }
                }, 120);
              }}
              className="h-12 rounded-lg bg-[#1a56db] px-6 text-white text-[14px] font-semibold hover:bg-[#143faa] shadow-none"
              data-testid="services-bottom-cta-score"
            >
              Get My Opportunity Score
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/contact?intent=book")}
              className="h-12 rounded-lg border border-[#e5e7eb] bg-white px-6 text-[#111827] text-[14px] font-semibold hover:bg-[#f9fafb] shadow-none"
              data-testid="services-bottom-cta-book"
            >
              <Calendar className="mr-2 h-4 w-4" />
              Book a Free Call
            </Button>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export { ServicesContent };
export default ServicesContent;
